// WHICH PERSON OF A REPORT A TEST IS FOR (TGCLOUD-1868, Phase E4; plan, "Upload of a multi-individual
// report on a Test"). Pure: no React, no requests.
//
// A trio's or a prenatal report gives a result for each person its results table names. Uploaded on
// a Test, intake does not guess whose the Test is: it answers who the report covers
// (`{ needsIndividual: true, individuals: [{ key, role, resultAsPrinted, anchors }], proposedKey? }`,
// labReportIntake.needsIndividualAnswer), with no token and no proposal, and the view asks
//
//   "This report covers 3 individuals. Which one is this Test for?"
//
// listing each person by role (labReportInboxDisplay.individualLabel, the inbox's own words), each
// with their Result cell as printed and their row in the report, and NOTHING PRESELECTED. The one the
// identity check proposes (`proposedKey`: the one person whose printed identity agrees with this
// record) is labelled "Matches this person’s details", and is still not chosen for the clinician.
//
// WHO IS OFFERED is the server's rule (labReportIntake.individualFitProblem, bound by
// whichIndividual.test.js): a FETUS's result only on a Test of its pregnancy AND its fetus (both
// named); a twin's or a baby's the report does not say is a fetus or a born child on NO Test
// (UNDETERMINED_ROLES; TGCLOUD-1868 review); ANY OTHER person's only on a Test that is not about a
// pregnancy (prenatalScope.carriesPrenatalScope). Whoever is not offered is said, with why, never
// left out silently. Choosing reads the report again for that person
// (useLabReportReview.chooseIndividual).
//
// ONCE A PERSON'S PART IS READ (by an upload's choice, or the inbox's hand-off), the view says whose
// it is and keeps everyone else's apart:
//   - the Test pane says "This Test is for: Mother" (testIsForText);
//   - the report pane draws every OTHER person's row distinctly, hatched and dashed and tagged
//     "Another individual’s result: not this Test" (markOtherIndividualRows), so a value is never
//     checked against the wrong row. The answer's notes on what was not read hold each such row, at
//     its place (backend proposal.notReadForIndividual), worded as ANOTHER_INDIVIDUALS_RESULT_NOTE:
//     a copy of the reader's constant (labReports/index.js), bound by whichIndividual.test.js.
//
// THE WORDS ARE IN THE SELECTED LANGUAGE (TGCLOUD-1893). The constants stay English, the keys, and are
// translated where they are shown. Each helper that says a sentence takes the caller's `t` as its
// last argument; left out, it is the current language's (`translate`), as the inbox's own helpers
// say a person (labReportInboxDisplay.individualLabel), so the Test form's calls are translated too.
// choiceWaitText takes none: it is mapped over the reviews, and a map passes an index there.

import { translate } from 'src/hooks/useTranslate';
import { carriesPrenatalScope } from 'src/components/pedigree-chart/pedigree-sidebar/sections/clinical-notes-section/forms/prenatalScope';
import { roleOfIndividualKey } from 'src/components/pedigree-chart/pedigree-sidebar/sections/clinical-notes-section/labReportInboxLink';
import {
  individualLabel,
  individualNoun
} from 'src/components/lab-report-inbox/labReportInboxDisplay';
import { UNDETERMINED_ROLES } from 'src/components/lab-report-inbox/labReportInboxVocabulary';

const hasText = (v) => typeof v === 'string' && v.trim() !== '';

/** A person's role: their own, else the one their key is (`fetus`, `mother`, `fetus-2` ...). */
const roleOf = (individual) =>
  (individual && individual.role) || roleOfIndividualKey(individual?.key);

/** Whether a person of a report is a fetus: the role decides, as the server's rule does. */
export const isFetus = (individual) => roleOf(individual) === 'fetus';

/** Whether a person of a report is a twin or a baby the report does not say is a fetus or born. */
export const isUndetermined = (individual) =>
  UNDETERMINED_ROLES.includes(roleOf(individual));

/**
 * Why a person of the report does not fit a Test of this prenatal scope, or null when they do:
 * 'undetermined' (a twin's or a baby's the report does not say is a fetus or a born child, on any
 * Test), 'fetus' (a fetus's result, on a Test not of a pregnancy and one of its fetuses), 'other'
 * (anyone else's, on a Test about a pregnancy).
 *
 * @param {{ key, role }} individual
 * @param {{ pregnancyId?, fetusId? }} testScope the Test's, as it will be saved
 * @returns {'undetermined'|'fetus'|'other'|null}
 */
export function misfitOf(individual, testScope) {
  const scope = testScope || {};
  if (isUndetermined(individual)) return 'undetermined';
  if (isFetus(individual)) {
    return hasText(String(scope.pregnancyId ?? '')) &&
      hasText(String(scope.fetusId ?? ''))
      ? null
      : 'fetus';
  }
  return carriesPrenatalScope(scope) ? 'other' : null;
}

/** "This report covers 3 individuals. Which one is this Test for?": always two or more. */
export const whichIndividualQuestion = (count, t = translate) =>
  t('This report covers {{count}} individuals. Which one is this Test for?', {
    count
  });

export const PROPOSED_LABEL = 'Matches this person’s details';
export const NOTHING_CHOSEN = 'Choose who this Test is for.';
export const RESULT_AS_PRINTED = 'Result, as printed';

// Why the people not offered are not, said once for all of them.
export const NOT_OFFERED_TEXT = Object.freeze({
  // A Test that is not about a pregnancy: the fetus's result is not offered.
  fetus:
    'A fetus’s result is not offered here: it is read into a Test added for the fetus from its mother’s pregnancy.',
  // A Test about a pregnancy and one fetus: nobody else's result is offered.
  other:
    'This Test is about a pregnancy, so only a fetus’s result is offered. The other people on the report are read into Tests of their own.',
  // ...and the report gives no fetus's result.
  otherNoFetus:
    'This Test is about a pregnancy, so only a fetus’s result can be read into it, and this report gives none. The people on the report are read into Tests of their own.',
  // A Test about a pregnancy that names no fetus: nobody's result fits it.
  none: 'This Test is about a pregnancy but names no fetus, so no result on this report can be read into it. A fetus’s result is read into a Test added for that fetus; anyone else’s into a Test of their own.',
  // A twin or a baby the report does not say is a fetus or a born child: on no Test.
  undetermined:
    'A twin’s or a baby’s result is not offered on any Test: the report does not say whether they are a fetus or a born child. Enter it by hand, as the report gives it.'
});

// Why Save waits while the choice is open (TGCLOUD-1868, Phase E routine call): the Test would
// otherwise be saved without the report, which then could only be uploaded again.
export const SAVE_WAITS_FOR_CHOICE =
  'Choose who on the report this Test is for, or dismiss the report.';
export const SAVE_WAITS_FOR_READING =
  'Wait while the report is read for the person chosen.';

/**
 * Whether a review is still putting the choice: asking whose the Test is, or reading the report for
 * the person chosen.
 */
export const isChoiceOpen = (review) =>
  !!review &&
  (review.status === 'choosing' ||
    (review.status === 'reading' && !!review.choice));

/** Why Save waits for a review still putting the choice, or null, in the current language. */
export const choiceWaitText = (review) =>
  !isChoiceOpen(review)
    ? null
    : review.status === 'choosing'
    ? translate(SAVE_WAITS_FOR_CHOICE)
    : translate(SAVE_WAITS_FOR_READING);

// "Send to the inbox instead" while the choice is open (TGCLOUD-1868, Phase E routine call): the file
// itself, read for nobody, so nobody need be chosen first (TGCLOUD-1868 review).
export const SEND_CHOICE_TO_INBOX_TEXT =
  'Not this person’s report? It goes to the Lab Report Inbox, where each person on it is matched on their own, and nothing from it is added to this Test.';

/** The words for a person's choice, and "Read the mother’s result into this Test". */
export const readIntoTestLabel = (individual, t = translate) =>
  individual
    ? t('Read {{person}}’s result into this Test', {
        person: individualNoun(individual)
      })
    : t('Read the chosen result into this Test');

/** What a person's row in the report pane is named by, for a screen reader. */
export const rowLabelOf = (individual, t = translate) =>
  t('{{person}}’s result', { person: individualLabel(individual) });

/**
 * The choice to put to the clinician.
 *
 * @param {{ individuals: object[], proposedKey?: string|null, testScope?: object }} args intake's
 *   answer (`individuals`, `proposedKey`) and the Test's prenatal scope
 * @returns {{ question: string, offered: object[], notOffered: object[], whyNotOffered: string|null }}
 *   each person `{ individual, index, label, resultAsPrinted, paths, proposed }`: `index` their place
 *   in the answer, `paths` their row's boxes in the report pane, `proposed` true only for the one
 *   the identity check proposes, and only where they are offered
 */
export function choiceOf({
  individuals = [],
  proposedKey = null,
  testScope = {},
  t = translate
}) {
  const people = (Array.isArray(individuals) ? individuals : [])
    .map((individual, index) => ({ individual, index }))
    .filter(({ individual }) => individual && hasText(individual.key));
  const entry = ({ individual, index }, offered) => ({
    individual: { key: individual.key, role: roleOf(individual) },
    index,
    label: individualLabel({ key: individual.key, role: roleOf(individual) }),
    resultAsPrinted: hasText(individual.resultAsPrinted)
      ? individual.resultAsPrinted
      : null,
    paths: [
      ...new Set(
        (Array.isArray(individual.anchors) ? individual.anchors : [])
          .map((a) => a && a.path)
          .filter(hasText)
      )
    ],
    proposed: offered && !!proposedKey && individual.key === proposedKey
  });
  const offered = [];
  const notOffered = [];
  const reasons = new Set();
  for (const person of people) {
    const misfit = misfitOf(person.individual, testScope);
    if (misfit) {
      reasons.add(misfit);
      notOffered.push(entry(person, false));
    } else {
      offered.push(entry(person, true));
    }
  }
  const byScope = !(reasons.has('fetus') || reasons.has('other'))
    ? null
    : !offered.length && reasons.has('fetus') && reasons.has('other')
    ? NOT_OFFERED_TEXT.none
    : reasons.has('other')
    ? offered.length
      ? NOT_OFFERED_TEXT.other
      : NOT_OFFERED_TEXT.otherNoFetus
    : NOT_OFFERED_TEXT.fetus;
  // Each sentence said on its own, then put side by side.
  const whyNotOffered = !notOffered.length
    ? null
    : [
        byScope,
        reasons.has('undetermined') ? NOT_OFFERED_TEXT.undetermined : null
      ]
        .filter(Boolean)
        .map((sentence) => t(sentence))
        .join(' ');
  return {
    question: whichIndividualQuestion(people.length, t),
    offered,
    notOffered,
    whyNotOffered
  };
}

/**
 * The boxes of the report pane while the choice is put: each person's row, named for them. The paths
 * are the answer's own (`individuals.<i>`), about no value on the Test.
 */
export function choiceAnchors(individuals = [], t = translate) {
  return (Array.isArray(individuals) ? individuals : []).flatMap((individual) =>
    individual && Array.isArray(individual.anchors)
      ? individual.anchors.map((anchor) => ({
          ...anchor,
          label: rowLabelOf(
            {
              key: individual.key,
              role: roleOf(individual)
            },
            t
          )
        }))
      : []
  );
}

// ── Once one person's part is read ─────────────────────────────────────────────────────────────

/**
 * The note one person's Test carries for each row of the results table that is someone else's
 * (backend labReports/index.js ANOTHER_INDIVIDUALS_RESULT, word for word: whichIndividual.test.js
 * reads it from there).
 */
export const ANOTHER_INDIVIDUALS_RESULT_NOTE =
  'Another individual’s result, not this Test: this row of the results table gives the result of someone else the report tested, so nothing in it was read into this Test.';

/** What another person's row is called in the report pane, on it and to a screen reader. */
export const ANOTHER_INDIVIDUAL_LABEL =
  'Another individual’s result: not this Test';

/** How the report pane draws another person's row (ReportViewer `tone`). */
export const OTHER_INDIVIDUAL_TONE = 'other-individual';

/** Whether a note on what was not read is another person's row of the results table. */
export const isAnotherIndividualsRow = (note) =>
  !!note &&
  typeof note === 'object' &&
  note.note === ANOTHER_INDIVIDUALS_RESULT_NOTE;

/**
 * The report pane's boxes with every other person's row marked as theirs: drawn distinctly
 * (`tone`), tagged in words on the report (`tag`) and named so to a screen reader (`label`). The
 * rest are as they were, and the list is the same list where there is nothing to mark.
 *
 * @param {Array<{ path }>} anchors the answer's boxes, by its paths (`notRead.<i>` for a note)
 * @param {object[]} notRead the answer's notes on what was not read
 */
export function markOtherIndividualRows(anchors, notRead, t = translate) {
  const list = Array.isArray(anchors) ? anchors : [];
  const notes = Array.isArray(notRead) ? notRead : [];
  if (!notes.some(isAnotherIndividualsRow)) return list;
  const words = t(ANOTHER_INDIVIDUAL_LABEL);
  return list.map((anchor) => {
    const at = /^notRead\.(\d+)$/.exec((anchor && anchor.path) || '');
    return at && isAnotherIndividualsRow(notes[Number(at[1])])
      ? {
          ...anchor,
          tone: OTHER_INDIVIDUAL_TONE,
          tag: words,
          label: words
        }
      : anchor;
  });
}

/**
 * The boxes of every OTHER person's rows on a SAVED Test's report (D6 with Phase E: GET
 * /test-results/:id/anchors `otherIndividuals: [{ key, role, anchors }]`), drawn as on a reading:
 * hatched and dashed (`tone`), tagged in words on the report (`tag`), and named for a screen reader
 * as that person's result, not this Test's (`label`). Each person's boxes go under a path of their
 * own, `individuals.<i>`, which is about no value on the Test (verificationTargets, anchorLabels), so
 * a box of someone else's is never taken for one of this Test's values, whatever path the answer
 * gave it.
 *
 * @param {Array<{ key, role, anchors: Array<{ path, page, rects }> }>} otherIndividuals
 */
export function savedOtherIndividualAnchors(otherIndividuals, t = translate) {
  return (Array.isArray(otherIndividuals) ? otherIndividuals : []).flatMap(
    (person, i) =>
      person && Array.isArray(person.anchors)
        ? person.anchors
            .filter((a) => a && Array.isArray(a.rects))
            .map((a) => ({
              path: `individuals.${i}`,
              page: a.page,
              rects: a.rects,
              tone: OTHER_INDIVIDUAL_TONE,
              tag: t(ANOTHER_INDIVIDUAL_LABEL),
              label: t('{{result}}: not this Test', {
                result: rowLabelOf(
                  {
                    key: person.key,
                    role: roleOf(person)
                  },
                  t
                )
              })
            }))
        : []
  );
}

/** Said under "This Test is for" on a saved Test whose report marks the other people's rows. */
export const OTHERS_MARKED_SAVED_TEXT =
  'The other people on the report are marked in it as another individual’s result, and nothing of theirs was read into this Test.';

/**
 * Whom a review's reading is for, `{ key, role }`, or null for a report about one person: the person
 * chosen, or handed over, else the one the reading names (`individual`, else `individualKey`).
 *
 * THE ROLE. The person's own where it is known; else the one the answer names (`individual: { key,
 * role }`: TGCLOUD-1868, Phase E routine call, "the link and answer carry each individual's role, so
 * a row-<n> person is named"), since a `row-<n>` key says no role and an inbox link carries only the
 * key; else the one the key says.
 */
export function forWhomOf(review) {
  const own = review && review.individual;
  const reading = (review && review.reading) || {};
  const answered =
    reading.individual && hasText(reading.individual.key)
      ? reading.individual
      : null;
  const key =
    (own && hasText(own.key) && own.key) ||
    (answered && answered.key) ||
    (hasText(reading.individualKey) && reading.individualKey) ||
    null;
  if (!key) return null;
  const role =
    (own && own.key === key && hasText(own.role) && own.role) ||
    (answered && answered.key === key && hasText(answered.role)
      ? answered.role
      : null) ||
    roleOfIndividualKey(key);
  return { key, role: role || null };
}

/** "This Test is for: Mother". */
export const testIsForText = (individual, t = translate) =>
  t('This Test is for: {{person}}', { person: individualLabel(individual) });

/** Said under it, while the report's other people are marked in it. */
export const OTHERS_MARKED_TEXT =
  'The other people on the report are marked in it as another individual’s result, and nothing of theirs is read into this Test.';
